import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Highcharts from "highcharts";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { Link, useOutletContext } from "react-router-dom";
import {
  fetchAnalyticsDashboardCore,
  fetchAnalyticsDashboardExtended,
  fetchAnalyticsNarrative,
  fetchInsightsAnomalies,
} from "../api/analyticsClient";
import { isAbortError } from "../utils/fetchAbort";
import { useTheme } from "../context/ThemeContext";
import { AdminHighcharts, adminPieCenterLabelColor } from "./chartTheme";
import ChartExplainAction from "./ChartExplainAction";
import { LENS_ROUTES } from "./routes";
import {
  buildAnalyticsChartExplainContexts,
  buildAnalyticsDropOffExplainContexts,
  formatChartExplanation,
} from "./chartExplainBuilders";
import "./survey.css";
import AiSummaryCard from "./AiSummaryCard";

function SectionHeader({ title, subtitle }) {
  return (
    <div className="flex items-baseline gap-3 mb-3">
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 tracking-tight">{title}</h2>
      {subtitle && <span className="text-xs text-slate-400">{subtitle}</span>}
    </div>
  );
}

function XSmallIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ChevronDownIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function KpiIconSvg({ type }) {
  const cls = "w-4 h-4";
  if (type === "send") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <line strokeLinecap="round" strokeLinejoin="round" x1="22" y1="2" x2="11" y2="13" />
      <polygon strokeLinecap="round" strokeLinejoin="round" points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
  if (type === "mail-open") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
  if (type === "clock") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" /><polyline strokeLinecap="round" strokeLinejoin="round" points="12 6 12 12 16 14" />
    </svg>
  );
  if (type === "check-circle") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  if (type === "zap") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
  if (type === "activity") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline strokeLinecap="round" strokeLinejoin="round" points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
  if (type === "timer") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  return null;
}

function KpiCardSkeleton() {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/80 shadow-sm p-4 animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="h-8 w-8 rounded-xl bg-slate-200 dark:bg-slate-700" />
      </div>
      <div className="h-7 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
      <div className="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded" />
    </div>
  );
}

function KpiCard({ label, value, iconType, iconColor, iconBg, loading, linkTo }) {
  if (loading) return <KpiCardSkeleton />;
  const inner = (
    <div className="rounded-2xl bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/80 shadow-sm p-4 hover:shadow-md hover:border-slate-200 dark:hover:border-slate-600 transition-all duration-200 h-full">
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl ${iconBg} ${iconColor}`}>
          <KpiIconSvg type={iconType} />
        </span>
      </div>
      <div className="text-[1.6rem] font-bold text-slate-900 dark:text-slate-100 tabular-nums leading-none mb-1.5">
        {value ?? "—"}
      </div>
      <div className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-snug">{label}</div>
    </div>
  );
  if (linkTo) {
    return (
      <Link to={linkTo} className="block h-full">
        {inner}
      </Link>
    );
  }
  return inner;
}

function PageSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#004f73]/20 border-t-[#004f73]" />
    </div>
  );
}

function AnalyticsNarrativePanel({ narrative, narrativeLoading, narrativeSource, onGenerate, dataReady }) {
  return (
    <AiSummaryCard
      narrative={narrative}
      narrativeLoading={narrativeLoading}
      narrativeSource={narrativeSource}
      onGenerate={onGenerate}
      dataReady={dataReady}
      emptyHint="Generate an AI summary from the loaded analytics."
      loadingHint="Summary available after analytics data loads."
      readDisabled={!narrative && narrativeLoading}
    />
  );
}

function getAnalyticsTableCustomStyles(dark) {
  const headBg = dark ? "rgba(255, 255, 255, 0.06)" : "#f8fafc";
  const headColor = dark ? "#cbd5e1" : "#475569";
  const cellColor = dark ? "#e2e8f0" : "#1e293b";
  const borderColor = dark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0";
  const rowBorder = dark ? "rgba(255, 255, 255, 0.06)" : "#f1f5f9";

  return {
    table: {
      style: {
        minWidth: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
      },
    },
    headRow: {
      style: {
        minHeight: "40px",
        backgroundColor: headBg,
        borderBottomWidth: "1px",
        borderBottomColor: borderColor,
      },
    },
    headCells: {
      style: {
        fontSize: "11px",
        fontWeight: 700,
        color: headColor,
        background: headBg,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    rows: {
      style: {
        fontSize: "12.5px",
        color: cellColor,
        minHeight: "40px",
        borderBottomWidth: "1px",
        borderBottomColor: rowBorder,
        transition: "background-color 0.12s ease",
      },
      highlightOnHoverStyle: {
        backgroundColor: dark ? "rgba(34, 211, 238, 0.08)" : "rgba(0,79,115,0.04)",
        cursor: "default",
        transition: "background-color 0.12s ease",
      },
    },
    cells: {
      style: {
        color: cellColor,
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    pagination: {
      style: {
        borderTopWidth: "1px",
        borderTopColor: borderColor,
        fontSize: "13px",
        color: headColor,
        backgroundColor: dark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
      },
      pageButtonsStyle: {
        borderRadius: "8px",
        color: dark ? "#67e8f9" : "#004f73",
        fill: dark ? "#67e8f9" : "#004f73",
      },
    },
  };
}

function PillarStuckList({ funnel, title, emptyMessage = "No one has started yet.", chartId, onExplain, explanation, explainSource, explainLoading }) {
  const steps = Array.isArray(funnel?.steps) ? funnel.steps : [];
  const started = funnel?.started_count || 0;
  const completed = funnel?.completed_count || 0;
  const topDrop = funnel?.top_drop_off;

  return (
    <div className="relative p-5 admin-chart-card bg-white rounded-2xl border border-slate-100 shadow-sm h-full">
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <div className="flex items-center gap-2 shrink-0">
          {started > 0 && (
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              {funnel?.completion_pct || 0}% done
            </span>
          )}
          {chartId && (
            <ChartExplainAction
              chartId={chartId}
              onExplain={onExplain}
              explanation={explanation}
              source={explainSource}
              loading={Boolean(explainLoading)}
              disabled={!started}
              containerClassName="relative"
            />
          )}
        </div>
      </div>
      {started > 0 ? (
        <p className="text-xs text-slate-500">
          {started} started · {completed} finished
        </p>
      ) : (
        <p className="text-xs text-slate-400 mt-1">{emptyMessage}</p>
      )}

      {topDrop?.drop_off_count > 0 ? (
        <div className="mt-3.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900">
          <span className="font-semibold">Most drop off at:</span>{" "}
          {topDrop.title}{" "}
          <span className="text-amber-700 font-semibold">({topDrop.drop_off_pct}%)</span>
        </div>
      ) : null}

      {started > 0 ? (
        <div className="mt-4 space-y-3.5">
          {steps.map((step) => (
            <div key={step.step}>
              <div className="flex items-center justify-between gap-3 text-xs mb-1.5">
                <span className="font-medium text-slate-700">{step.title}</span>
                <span className={`shrink-0 font-semibold ${step.drop_off_count > 0 ? "text-amber-600" : "text-slate-400"}`}>
                  {step.drop_off_count > 0 ? `${step.drop_off_count} stopped` : "—"}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${step.drop_off_pct > 20 ? "bg-amber-500" : step.drop_off_pct > 0 ? "bg-amber-300" : "bg-slate-200"}`}
                  style={{ width: `${Math.min(step.drop_off_pct || 0, 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function LiteConversionSummary({ conversion, onExplain, explanation, explainSource, explainLoading }) {
  const liteFinished = conversion?.lite_finished || 0;
  const fullStarted = conversion?.full_started || 0;
  const fullCompleted = conversion?.full_completed || 0;
  const awaiting = conversion?.awaiting_upgrade || 0;
  const declined = conversion?.upgrade_declined || 0;

  const pct = (count) =>
    liteFinished > 0 ? Math.round((count / liteFinished) * 100) : 0;

  const stages = [
    { label: "Finished Lite", count: liteFinished, pct: liteFinished > 0 ? 100 : 0, color: "text-brand" },
    { label: "Started full", count: fullStarted, pct: pct(fullStarted), color: "text-violet-600" },
    { label: "Completed full", count: fullCompleted, pct: pct(fullCompleted), color: "text-emerald-600" },
  ];

  return (
    <div className="p-5 admin-chart-card bg-white rounded-2xl border border-slate-100 shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-sm font-semibold text-slate-900">Lite → Full upgrade</p>
        <div className="flex items-center gap-2 shrink-0">
          {liteFinished > 0 && (
            <span className="text-[11px] font-semibold text-brand bg-brand/8 border border-brand/20 rounded-full px-2 py-0.5">
              {pct(fullStarted)}% converted
            </span>
          )}
          <ChartExplainAction
            chartId="lite_to_full_conversion"
            onExplain={onExplain}
            explanation={explanation}
            source={explainSource}
            loading={Boolean(explainLoading)}
            disabled={!liteFinished}
            containerClassName="relative"
          />
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Of people who finish Lite, how many go on to the full assessment?</p>

      {liteFinished === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">No Lite assessments finished yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            {stages.map((stage, index) => (
              <div key={stage.label} className="relative">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center">
                  <div className={`text-2xl font-bold tabular-nums ${stage.color}`}>{stage.count}</div>
                  <div className="text-[11px] font-medium text-slate-600 mt-0.5 leading-tight">{stage.label}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{stage.pct}%</div>
                </div>
                {index < stages.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-2 w-4 h-4 items-center justify-center text-slate-300 text-sm -translate-y-1/2 z-10" aria-hidden>
                    →
                  </div>
                )}
              </div>
            ))}
          </div>

          {(awaiting > 0 || declined > 0) && (
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
              {awaiting > 0 && (
                <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 text-amber-700 font-medium">
                  {awaiting} still deciding
                </span>
              )}
              {declined > 0 && (
                <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1 font-medium">
                  {declined} chose "later"
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const AnalyticsPage = ({ embedded = false }) => {
  const outletContext = useOutletContext();
  const setAdminAssistantContext = outletContext?.setAdminAssistantContext;
  const { theme } = useTheme();
  const dark = theme === "dark";
  const analyticsTableCustomStyles = useMemo(() => getAnalyticsTableCustomStyles(dark), [dark]);
  const [pieData, setPieData] = useState({
    total: 0,
    seriesName: "Assessment Status",
    seriesData: [],
  });
  const [barData, setBarData] = useState([]);
  const [filterType, setFilterType] = useState("months");
  const [isLoading, setIsLoading] = useState(true);
  const [extendedLoading, setExtendedLoading] = useState(true);
  const [inviteData, setInviteData] = useState({
    total_invites_sent: 0,
    total_invites_accepted: 0,
    pending_invites: 0,
  });
  const [surveyData, setSurveyData] = useState({
    total_surveys_submitted: 0,
    total_full_completed: 0,
    total_lite_completed: 0,
    total_in_process_surveys: 0,
    average_completion_time: 0,
  });
  const [surveyTable, setSurveyTable] = useState([]);
  const [dropOffData, setDropOffData] = useState({
    full_pillar_funnel: { started_count: 0, completed_count: 0, completion_pct: 0, steps: [], top_drop_off: null },
    lite_pillar_funnel: { started_count: 0, completed_count: 0, completion_pct: 0, steps: [], top_drop_off: null },
    lite_to_full: {
      lite_in_progress: 0,
      lite_finished: 0,
      full_started: 0,
      full_completed: 0,
      upgrade_declined: 0,
      awaiting_upgrade: 0,
      conversion_pct: 0,
      full_completion_pct: 0,
    },
  });
  const [chartExplainById, setChartExplainById] = useState({});
  const [chartExplainLoadingById, setChartExplainLoadingById] = useState({});
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeSource, setNarrativeSource] = useState("");
  const [anomalies, setAnomalies] = useState([]);
  const [anomaliesDismissed, setAnomaliesDismissed] = useState(false);
  const aiSummaryBusyRef = useRef(false);

  const analyticsContext = useMemo(
    () => ({
      scope: {
        label: "Analytics dashboard",
        text: `Current ${filterType} trend and participant status distribution`,
      },
      kpis: {
        invitesSent: inviteData?.total_invites_sent || 0,
        invitesOpened: inviteData?.total_invites_accepted || 0,
        pendingInvites: inviteData?.pending_invites || 0,
        completedAssessments: surveyData?.total_surveys_submitted || 0,
        fullCompletedAssessments: surveyData?.total_full_completed || 0,
        liteCompletedAssessments: surveyData?.total_lite_completed || 0,
        inProcessAssessments: surveyData?.total_in_process_surveys || 0,
        avgCompletionTimeHours: surveyData?.average_completion_time || 0,
      },
      statusDistribution: pieData?.seriesData || [],
      inviteTrend: barData || [],
      pillarFunnel: dropOffData?.full_pillar_funnel?.steps || [],
      liteToFull: dropOffData?.lite_to_full || {},
      organizationRows: surveyTable.slice(0, 200).map((r) => ({
        name: `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Participant",
        role: r.designation || "",
        status: r.status || "",
        completion_time: r.completion_time || null,
        created_date: r.created_date || "",
        completion_date: r.completion_date || "",
      })),
      filters: { trendGranularity: filterType },
    }),
    [filterType, inviteData, surveyData, pieData, barData, surveyTable, dropOffData]
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const emptyInvite = {
      total_invites_sent: 0,
      total_invites_accepted: 0,
      pending_invites: 0,
      emails_opened: 0,
      assessments_started: 0,
    };
    const emptySurvey = {
      total_surveys_submitted: 0,
      total_full_completed: 0,
      total_lite_completed: 0,
      total_in_process_surveys: 0,
      average_completion_time: 0,
    };

    const emptyDropOff = {
      full_pillar_funnel: { started_count: 0, completed_count: 0, completion_pct: 0, steps: [], top_drop_off: null },
      lite_pillar_funnel: { started_count: 0, completed_count: 0, completion_pct: 0, steps: [], top_drop_off: null },
      lite_to_full: {
        lite_in_progress: 0,
        lite_finished: 0,
        full_started: 0,
        full_completed: 0,
        upgrade_declined: 0,
        awaiting_upgrade: 0,
        conversion_pct: 0,
        full_completion_pct: 0,
      },
    };

    (async () => {
      setIsLoading(true);
      setExtendedLoading(true);

      const applyCore = (coreRes) => {
        const data = coreRes?.data;
        const inviteRaw = data?.invite;
        const surveyRaw = data?.survey;
        setInviteData(
          inviteRaw && typeof inviteRaw === "object" && !Array.isArray(inviteRaw)
            ? { ...emptyInvite, ...inviteRaw }
            : { ...emptyInvite }
        );
        setSurveyData(
          surveyRaw && typeof surveyRaw === "object" && !Array.isArray(surveyRaw)
            ? { ...emptySurvey, ...surveyRaw }
            : { ...emptySurvey }
        );
        setBarData(Array.isArray(data?.chart) ? data.chart : []);

        const dist =
          data?.status_distribution &&
          typeof data.status_distribution === "object" &&
          !Array.isArray(data.status_distribution)
            ? data.status_distribution
            : null;
        setPieData({
          total: dist
            ? Object.values(dist).reduce((acc, val) => acc + Number(val || 0), 0)
            : 0,
          seriesName: "Assessment Status",
          seriesData: dist
            ? Object.entries(dist).map(([key, value]) => ({
                name:
                  key === "lite_completed"
                    ? "Lite completed"
                    : key === "completed"
                      ? "Full completed"
                      : key.replace(/_/g, " "),
                y: value,
              }))
            : [],
        });
      };

      const applyExtended = (extRes) => {
        const ext = extRes?.data;
        setSurveyTable(Array.isArray(ext?.table) ? ext.table : []);
        setDropOffData(
          ext?.drop_off && typeof ext.drop_off === "object" && !Array.isArray(ext.drop_off)
            ? { ...emptyDropOff, ...ext.drop_off }
            : { ...emptyDropOff }
        );
      };

      const corePromise = fetchAnalyticsDashboardCore(filterType, { signal })
        .then((coreRes) => {
          if (signal.aborted) return;
          applyCore(coreRes);
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          toast.error("Could not load analytics.");
        })
        .finally(() => {
          if (!signal.aborted) setIsLoading(false);
        });

      const extendedPromise = fetchAnalyticsDashboardExtended({ signal })
        .then((extRes) => {
          if (signal.aborted) return;
          applyExtended(extRes);
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          toast.error("Could not load participant table and drop-off data.");
        })
        .finally(() => {
          if (!signal.aborted) setExtendedLoading(false);
        });

      await Promise.allSettled([corePromise, extendedPromise]);
    })();

    return () => controller.abort();
  }, [filterType]);

  const dynamicColumns = [
    {
      name: <span className="flex items-center">First Name</span>,
      selector : (row) => row.first_name,
      cell: (row) => <span>{row.first_name || "-"}</span>,
      sortable: true,
    },
    {
      name: <span className="flex items-center">Last Name</span>,
      selector : (row) => row.last_name,
      cell: (row) => <span>{row.last_name || "-"}</span>,
      sortable: true,
    },
    {
      name: <span className="flex items-center">Role</span>,
      selector: (row) => row.designation,
      cell: (row) => <span>{row.designation || "-"}</span>,
      sortable: true,
      width: "140px",
    },
    {
      name: <span className="flex items-center">Created Date</span>,
      selector: (row) => row.created_date,
      sortable: true,
      cell: (row) => (
        <div className="truncate">
          <span>{row.created_date || "-"}</span>
        </div>
      ),
    },
    {
      name: <span className="flex items-center">Completed On</span>,
      selector : (row) => row.completion_date, 
      cell: (row) => (
        <div className="truncate">
          <span>{row.completion_date || "-"}</span>
        </div>
      ),
      sortable: true,
    },
    {
      name: <span className="flex items-center">Completed Time (Hrs)</span>,
      selector : (row) => row.completion_time, 
      cell: (row) => (
        <div className="truncate">
          <span>
            {row.completion_time ? `${row.completion_time} ` : "-"}
          </span>
        </div>
      ),
      sortable: true,
      width : "110px"
    },
    {
      name: <span className="flex items-center">Status</span>, 
      selector : (row) => row.status, // Return just the status for sorting
      sortable: true,
      cell: (row) => (
        <span className="capitalize">
          {row.status === "LiteCompleted" ? "Lite completed" : row.status}
        </span>
      ),
      width: "120px", 
    },
  ];

  const generateBarChart = (data) => {
    if (!data || !Array.isArray(data)) {
      return {};
    }
    // const categories = data.map((item) => item.month);
    const categories = data?.map((item) => {
      if (filterType === "months") {
        return item.month; // e.g., "January", "February"
      } else if (filterType === "weeks") {
        return item.day; // e.g., "Week 1", "Week 2"
      } else if (filterType === "days") {
        return item.hour; // e.g., "00:00", "01:00"
      }
      return null;
    });

    const seriesNames = ["pending", "invite_sent", "lite_completed", "completed"];
    const seriesLabels = {
      pending: "Pending",
      invite_sent: "Invite Sent",
      lite_completed: "Lite Completed",
      completed: "Full Completed",
    };
    const series = seriesNames?.map((name, index) => ({
      name: seriesLabels[name] || name?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      data: data?.map((item) => item[name] || 0),
      color: ["#42BBFF", "#004F73", "#3b82f6", "#004F73"][index],
    }));

    return {
      chart: {
        type: "column",
      },
      credits: {
        enabled: false,
      },
      title: {
        text: "",
        align: "left",
        verticalAlign: "top",
        style: {
          fontWeight: "500",
        },
      },
      xAxis: {
        categories,
        crosshair: true,
        accessibility: {
          description: "Months",
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: "Invite count",
        },
      },
      tooltip: {
        valueSuffix: " Count",
      },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
        },
      },
      series,
    };
  };

  const generatePieChart = (data) => {
    const seriesData = Array.isArray(data?.seriesData) ? data.seriesData : [];
    return {
      chart: {
        type: "pie",
        custom: {},
        events: {
          render() {
            const chart = this;
            const series = chart.series[0];
            let customLabel = chart.options.chart.custom.label;

            if (!customLabel) {
              customLabel = chart.options.chart.custom.label = chart.renderer
                .label(`Total<br/><strong>${100} %</strong>`, 0, 0)
                .css({
                  color: adminPieCenterLabelColor(dark),
                  textAnchor: "middle",
                })
                .add();
            }

            const x = series.center[0] + chart.plotLeft;
            const y =
              series.center[1] + chart.plotTop - customLabel.attr("height") / 2;

            customLabel.attr({
              x,
              y,
            });

            customLabel.css({
              fontSize: `${series.center[2] / 12}px`,
            });
          },
        },
      },
      title: {
        text: "Current Statistics",
        align: "left",
        verticalAlign: "top",
        style: {
          fontWeight: "500", // Set font weight to 400
        },
      },
      accessibility: {
        point: {
          valueSuffix: "%",
        },
      },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.percentage:.2f}%</b>",
      },
      legend: {
        enabled: true,
        layout: "horizontal", // Layout the legend horizontally
        align: "center", // Align legend to the center
        verticalAlign: "bottom", // Place it below the chart
        itemStyle: {
          fontSize: "14px", // Optional: Adjust font size for better visibility
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          innerSize: "75%",
          dataLabels: {
            enabled: false, // Disable data labels to remove pointing lines
          },
        },
      },
      series: [
        {
          name: data?.seriesName || "Data",
          colorByPoint: true,
          data: seriesData,
          colors: ["#94a3b8", "#42BBFF", "#3b82f6", "#004F73"],
        },
      ],
      credits: {
        enabled: false,
      },
    };
  };

  const chartExplainContextById = useMemo(
    () => ({
      ...buildAnalyticsChartExplainContexts({ barData, filterType, pieData }),
      ...buildAnalyticsDropOffExplainContexts({ dropOffData }),
    }),
    [barData, filterType, pieData, dropOffData]
  );

  useEffect(() => {
    setChartExplainById({});
  }, [chartExplainContextById]);

  const handleExplainChart = (chartId) => {
    const context = chartExplainContextById[chartId];
    if (!context) return;
    setChartExplainLoadingById((prev) => ({ ...prev, [chartId]: true }));
    const explanation = formatChartExplanation(context);
    setChartExplainById((prev) => ({
      ...prev,
      [chartId]: { explanation, source: "structured" },
    }));
    setChartExplainLoadingById((prev) => ({ ...prev, [chartId]: false }));
  };

  const loadAiSummary = useCallback(async () => {
    if (isLoading || extendedLoading || aiSummaryBusyRef.current) return;
    aiSummaryBusyRef.current = true;
    setNarrativeLoading(true);
    setAnomaliesDismissed(false);
    try {
      const [narrativeRes, anomalyRes] = await Promise.allSettled([
        fetchAnalyticsNarrative(analyticsContext),
        fetchInsightsAnomalies(analyticsContext),
      ]);
      if (narrativeRes.status === "fulfilled") {
        setNarrative(narrativeRes.value?.data?.narrative || "");
        setNarrativeSource(narrativeRes.value?.data?.source || "");
      } else {
        setNarrative("");
        setNarrativeSource("");
      }
      if (anomalyRes.status === "fulfilled") {
        setAnomalies(anomalyRes.value?.data?.highlights || []);
      }
    } catch (err) {
      toast.error(err?.message || "Could not generate AI summary");
    } finally {
      aiSummaryBusyRef.current = false;
      setNarrativeLoading(false);
    }
  }, [analyticsContext, isLoading, extendedLoading]);

  useEffect(() => {
    setNarrative("");
    setNarrativeSource("");
    setAnomalies([]);
    setAnomaliesDismissed(false);
  }, [filterType]);

  useEffect(() => {
    if (isLoading || extendedLoading) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) loadAiSummary();
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [analyticsContext, isLoading, extendedLoading, loadAiSummary]);

  useEffect(() => {
    if (!setAdminAssistantContext) return;
    setAdminAssistantContext({
      page: "analytics",
      surface: embedded ? "portfolio" : "admin",
      currentRoute: embedded ? "/portfolio" : "/legacy/analytics",
      pagePath: embedded ? "/portfolio" : "/legacy/analytics",
      activeLens: embedded ? "operations" : null,
      ...analyticsContext,
    });
  }, [analyticsContext, embedded, setAdminAssistantContext]);

  if (isLoading) {
    return <PageSpinner />;
  }

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-slate-50/60"}>
      <div className={embedded ? "w-full" : "w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-6 pb-14"}>

        {!embedded ? (
        <>
        {/* ── Page header ── */}
        <header className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand/60 mb-2">
              Admin · Analytics
            </p>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight leading-tight">Analytics</h1>
            <p className="mt-1.5 text-sm text-slate-500">
              Workspace activity, assessment completion and drop-off trends.
            </p>
          </div>
        </header>
        </>
        ) : null}

        {/* ── AI Summary ── */}
        <AnalyticsNarrativePanel
          narrative={narrative}
          narrativeLoading={narrativeLoading}
          narrativeSource={narrativeSource}
          onGenerate={loadAiSummary}
          dataReady={!isLoading && !extendedLoading}
        />

        {/* ── AI Anomaly signals ── */}
        {anomalies.length > 0 && !anomaliesDismissed && (
          <div className="mb-5 rounded-2xl border border-amber-200/80 bg-amber-50/60 px-4 py-3" data-export-ignore>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <span className="text-xs font-semibold text-amber-800">AI signals · {anomalies.length} detected</span>
              </div>
              <button
                type="button"
                onClick={() => setAnomaliesDismissed(true)}
                className="text-amber-600 hover:text-amber-800 transition-colors shrink-0"
                aria-label="Dismiss signals"
              >
                <XSmallIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {anomalies.map((a, i) => (
                <div
                  key={i}
                  className={`inline-flex items-start gap-1.5 rounded-xl px-3 py-1.5 text-xs border ${
                    a.severity === "high"
                      ? "bg-red-50 border-red-200 text-red-800"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}
                >
                  <span className="font-semibold shrink-0">{a.title}:</span>
                  <span className="text-opacity-80">{a.detail}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Invitations + Assessment KPIs ── */}
        <div className="mb-6 w-full min-w-0">
        {/* ── Invitations KPIs ── */}
        <SectionHeader title="Invitations" subtitle="Sent · opened · started · not started" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label="Total invites sent"
            value={inviteData?.total_invites_sent || 0}
            iconType="send"
            iconColor="text-brand"
            iconBg="bg-brand/8"
            loading={isLoading}
            linkTo={LENS_ROUTES.engagementCompose}
          />
          <KpiCard
            label="Invites opened"
            value={inviteData?.emails_opened || 0}
            iconType="mail-open"
            iconColor="text-violet-600"
            iconBg="bg-violet-50"
            loading={isLoading}
          />
          <KpiCard
            label="Assessments started"
            value={inviteData?.assessments_started || 0}
            iconType="activity"
            iconColor="text-sky-600"
            iconBg="bg-sky-50"
            loading={isLoading}
          />
          <KpiCard
            label="Not started"
            value={inviteData?.pending_invites || 0}
            iconType="clock"
            iconColor="text-amber-600"
            iconBg="bg-amber-50"
            loading={isLoading}
          />
        </div>

        {/* ── Assessment KPIs ── */}
        <SectionHeader title="Assessments" subtitle="Full · Lite · in-progress · avg time" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label="Full assessments completed"
            value={surveyData?.total_full_completed || 0}
            iconType="check-circle"
            iconColor="text-emerald-600"
            iconBg="bg-emerald-50"
            loading={isLoading}
          />
          <KpiCard
            label="Lite assessments completed"
            value={surveyData?.total_lite_completed || 0}
            iconType="zap"
            iconColor="text-blue-500"
            iconBg="bg-blue-50"
            loading={isLoading}
          />
          <KpiCard
            label="In progress"
            value={surveyData?.total_in_process_surveys || 0}
            iconType="activity"
            iconColor="text-sky-600"
            iconBg="bg-sky-50"
            loading={isLoading}
          />
          <KpiCard
            label="Avg completion time"
            value={`${surveyData?.average_completion_time || 0} hr`}
            iconType="timer"
            iconColor="text-teal-600"
            iconBg="bg-teal-50"
            loading={isLoading}
          />
        </div>
        </div>

        {/* ── Participation trend chart ── */}
        <div className="mb-6 w-full min-w-0">
        <SectionHeader title="Participation trend" />
        <div className="admin-chart-card rounded-2xl border border-slate-200/80 bg-white shadow-sm mb-6 overflow-hidden">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="text-sm font-semibold text-slate-700">Invited participants</h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  id="filterType"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="admin-filter-select appearance-none bg-white border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand pl-3 pr-8 py-1.5 transition-colors shadow-sm"
                >
                  <option value="days">Daily</option>
                  <option value="weeks">Weekly</option>
                  <option value="months">Monthly</option>
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              </div>
              <ChartExplainAction
                chartId="invited_participants"
                onExplain={handleExplainChart}
                explanation={chartExplainById.invited_participants?.explanation}
                source={chartExplainById.invited_participants?.source}
                loading={Boolean(chartExplainLoadingById.invited_participants)}
                disabled={!barData?.length}
                containerClassName="relative"
              />
            </div>
          </div>
          <div className="px-2 pb-2">
            {barData && (
              <AdminHighcharts options={generateBarChart(barData)} />
            )}
          </div>
        </div>
        </div>

        {/* ── Participants table + status pie ── */}
        <div className="mb-6 w-full min-w-0">
        <SectionHeader title="Participants" subtitle="Assessment records · status distribution" />
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 mb-6">
          {/* Table — 8 cols */}
          <div className="xl:col-span-8 admin-chart-card rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
            <div className="px-4 pt-4 pb-2 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">Assessment records</h3>
            </div>
            {extendedLoading ? (
              <div className="px-4 py-10 text-sm text-slate-400 text-center">Loading participant records…</div>
            ) : (
            <DataTable
              className="analytics-assessment-table"
              columns={dynamicColumns}
              data={surveyTable}
              pagination
              highlightOnHover
              customStyles={analyticsTableCustomStyles}
              responsive={true}
              paginationPerPage={5}
              paginationRowsPerPageOptions={[5, 10]}
            />
            )}
          </div>

          {/* Pie chart — 4 cols */}
          <div className="xl:col-span-4 admin-chart-card rounded-2xl border border-slate-200/80 bg-white shadow-sm relative overflow-hidden">
            <div className="px-4 pt-4 pb-1 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">Status distribution</h3>
            </div>
            <AdminHighcharts options={generatePieChart(pieData)} />
            <ChartExplainAction
              chartId="current_statistics"
              onExplain={handleExplainChart}
              explanation={chartExplainById.current_statistics?.explanation}
              source={chartExplainById.current_statistics?.source}
              loading={Boolean(chartExplainLoadingById.current_statistics)}
              disabled={!pieData?.seriesData?.length}
            />
          </div>
        </div>
        </div>

        {/* ── Drop-off analytics ── */}
        <div className="mb-6 w-full min-w-0">
        <SectionHeader title="Drop-off analytics" subtitle="Where participants stop · Lite → Full conversion" />
        {extendedLoading ? (
          <div className="mb-6 rounded-2xl border border-slate-200/80 bg-white px-4 py-10 text-sm text-slate-400 text-center">
            Loading drop-off analytics…
          </div>
        ) : (
        <div className="flex flex-col gap-4 mb-6">
          <LiteConversionSummary
            conversion={dropOffData?.lite_to_full}
            onExplain={handleExplainChart}
            explanation={chartExplainById.lite_to_full_conversion?.explanation}
            explainSource={chartExplainById.lite_to_full_conversion?.source}
            explainLoading={Boolean(chartExplainLoadingById.lite_to_full_conversion)}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PillarStuckList
              funnel={dropOffData?.full_pillar_funnel}
              title="Full assessment"
              emptyMessage="No full assessments started yet."
              chartId="full_pillar_funnel"
              onExplain={handleExplainChart}
              explanation={chartExplainById.full_pillar_funnel?.explanation}
              explainSource={chartExplainById.full_pillar_funnel?.source}
              explainLoading={Boolean(chartExplainLoadingById.full_pillar_funnel)}
            />
            <PillarStuckList
              funnel={dropOffData?.lite_pillar_funnel}
              title="Lite assessment"
              emptyMessage="No Lite assessments started yet."
              chartId="lite_pillar_funnel"
              onExplain={handleExplainChart}
              explanation={chartExplainById.lite_pillar_funnel?.explanation}
              explainSource={chartExplainById.lite_pillar_funnel?.source}
              explainLoading={Boolean(chartExplainLoadingById.lite_pillar_funnel)}
            />
          </div>
        </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
